import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Send, Building2, User, CheckCircle2, Search, Check,
  Calendar, Clock, PartyPopper
} from 'lucide-react';
import submissionsService, { CreateSubmissionParams, Submission } from '../../services/submissions.service';
import submissionWorkflowService from '../../services/submissionWorkflow.service';
import jobOrdersService, { JobOrder } from '../../services/jobOrders.service';
import toast from 'react-hot-toast';

interface SubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  onSuccess?: () => void;
}

interface CompanyOption {
  id: string;
  name: string;
  industry?: string;
  billing_city?: string;
}

interface ContactOption {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  title?: string;
}

type ModalStep = 'form' | 'success';

export const SubmissionModal: React.FC<SubmissionModalProps> = ({
  isOpen,
  onClose,
  candidateId,
  candidateName,
  onSuccess,
}) => {
  const [companyResults, setCompanyResults] = useState<CompanyOption[]>([]);
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [selectedContactId, setSelectedContactId] = useState('');
  // Stellenauftrag: optional — ein Kandidat kann auch ohne konkrete Stelle
  // vorgestellt werden. Nur mit Auftrag erscheint der Vorschlag in dessen Pipeline.
  const [jobOrders, setJobOrders] = useState<JobOrder[]>([]);
  const [selectedJobOrderId, setSelectedJobOrderId] = useState('');
  const [autoSend, setAutoSend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Interview scheduling state
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewNotes, setInterviewNotes] = useState('');

  // Success step
  const [step, setStep] = useState<ModalStep>('form');
  const [createdSubmission, setCreatedSubmission] = useState<Submission | null>(null);
  const [savingInterview, setSavingInterview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setCreatedSubmission(null);
      setInterviewDate('');
      setInterviewTime('');
      setInterviewNotes('');
      searchCompanies('');
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCompany) {
      loadContacts(selectedCompany.id);
      loadJobOrders(selectedCompany.id);
    } else {
      setContacts([]);
      setSelectedContactId('');
      setJobOrders([]);
      setSelectedJobOrderId('');
    }
  }, [selectedCompany]);

  const searchCompanies = async (query: string) => {
    setSearchLoading(true);
    try {
      const searchParam = query ? `&search=${encodeURIComponent(query)}` : '';
      const res = await fetch(`/api/v1/companies?limit=200${searchParam}`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) {
        setCompanyResults(json.data || []);
      }
    } catch {
      console.error('Failed to search companies');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCompanySearchChange = (value: string) => {
    setCompanySearch(value);
    if (selectedCompany) {
      setSelectedCompany(null);
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      searchCompanies(value);
    }, 300);
  };

  const selectCompany = (company: CompanyOption) => {
    setSelectedCompany(company);
    setCompanySearch(company.name);
  };

  const loadJobOrders = async (companyId: string) => {
    try {
      const alle = await jobOrdersService.list({ client_company_id: companyId });
      // Nur Auftraege, die noch Vorschlaege annehmen
      setJobOrders((alle || []).filter(a => a.status === 'offen' || a.status === 'in_arbeit'));
    } catch {
      setJobOrders([]);
    }
  };

  const loadContacts = async (companyId: string) => {
    try {
      const res = await fetch(`/api/v1/contacts?linked_company_id=${companyId}&limit=100`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) {
        setContacts(json.data || []);
      }
    } catch {
      console.error('Failed to load contacts');
    }
  };

  const handleSubmit = async () => {
    console.log('[SubmissionModal] handleSubmit called', { selectedCompany: selectedCompany?.name, loading, candidateId });

    if (!selectedCompany) {
      toast.error('Bitte wähle ein Kundenunternehmen aus');
      return;
    }

    if (loading) return;

    setLoading(true);
    try {
      const params: CreateSubmissionParams = {
        candidate_id: candidateId,
        client_company_id: selectedCompany.id,
        client_contact_id: selectedContactId || undefined,
        job_posting_id: selectedJobOrderId || undefined,
        auto_send: autoSend,
      };

      console.log('[SubmissionModal] Creating submission...', params);
      const result = await submissionsService.create(params);
      console.log('[SubmissionModal] Submission created!', result);
      toast.success(autoSend ? 'Kandidat wurde vorgestellt!' : 'Entwurf gespeichert');
      onSuccess?.();
      // Ein Gespräch ist erst nach dem Versand möglich (Entwurf → Interview
      // lehnt der Prozess ab). Beim Entwurf daher direkt schließen.
      if (!autoSend) {
        handleClose();
        return;
      }
      setCreatedSubmission(result);
      setStep('success');
    } catch (err: any) {
      console.error('[SubmissionModal] Submit error:', err);
      toast.error(err.message || 'Fehler beim Erstellen der Submission');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInterview = async () => {
    if (!createdSubmission || !interviewDate) return;

    setSavingInterview(true);
    try {
      // Ortszeit des Browsers → ISO mit Zeitzone; ohne sie las der Server
      // die Uhrzeit als UTC (10:00 wurde zu 12:00).
      const interviewDatetime = new Date(
        `${interviewDate}T${interviewTime || '09:00'}:00`
      ).toISOString();

      // Statuswechsel laufen nur noch über den Prozess-Endpunkt; der frühere
      // PATCH /submissions/:id mit status wurde vom Backend mit 400 abgelehnt.
      // Der Prozess erlaubt ein Gespräch erst ab „Interview angefragt"; nach dem
      // Versand steht der Vorschlag auf „Vorgestellt". Den Zwischenschritt
      // regelkonform (mit Journaleintrag) setzen.
      try {
        await submissionWorkflowService.setzeStatus(createdSubmission.id, 'INTERVIEW_REQUESTED');
      } catch (err: any) {
        if (err?.code !== 'NO_CHANGE') throw err;
      }

      const ergebnis = await submissionWorkflowService.interviewAnlegen(createdSubmission.id, {
        scheduled_at: interviewDatetime,
      });
      const interviewId = ergebnis?.interview?.id;
      if (interviewId && interviewNotes.trim()) {
        await submissionWorkflowService.interviewAktualisieren(interviewId, {
          internal_notes: interviewNotes.trim(),
        });
      }

      toast.success('Interview geplant!');
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      console.error('[SubmissionModal] Interview save error:', err);
      toast.error(err.message || 'Fehler beim Speichern des Interviews');
    } finally {
      setSavingInterview(false);
    }
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setSelectedContactId('');
    setSelectedJobOrderId('');
    setAutoSend(false);
    setCompanySearch('');
    setCompanyResults([]);
    setStep('form');
    setCreatedSubmission(null);
    setInterviewDate('');
    setInterviewTime('');
    setInterviewNotes('');
    onClose();
  };

  if (!isOpen) return null;

  const renderFormStep = () => (
    <>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px', borderBottom: '1px solid var(--glass-border, #e5e7eb)',
        flexShrink: 0
      }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary, #111)', margin: 0 }}>
            Kandidat vorstellen
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', margin: '4px 0 0' }}>
            {candidateName} an ein Kundenunternehmen senden
          </p>
        </div>
        <button
          onMouseDown={e => { e.stopPropagation(); }}
          onClick={e => { e.stopPropagation(); handleClose(); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-tertiary, #9ca3af)', padding: 4
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Scrollable Body */}
      <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
        {/* Candidate chip */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px 6px 10px', borderRadius: 20,
          background: 'rgba(99,102,241,0.08)', marginBottom: 20,
          border: '1px solid rgba(99,102,241,0.15)'
        }}>
          <User size={14} style={{ color: '#6366f1' }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: '#6366f1' }}>{candidateName}</span>
        </div>

        {/* Company search */}
        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8,
            color: 'var(--text-primary, #111)'
          }}>
            Kundenunternehmen auswählen
          </label>

          {/* Search input */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={16} style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-tertiary, #9ca3af)', pointerEvents: 'none'
            }} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Name eingeben zum Suchen..."
              value={companySearch}
              onChange={e => handleCompanySearchChange(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 36px', borderRadius: 10,
                border: `2px solid ${selectedCompany ? '#10B981' : 'var(--glass-border, #e5e7eb)'}`,
                background: 'var(--bg-secondary, #f9fafb)',
                color: 'var(--text-primary, #111)', fontSize: 14,
                boxSizing: 'border-box', outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={e => {
                if (!selectedCompany) e.target.style.borderColor = '#6366f1';
              }}
              onBlur={e => {
                if (!selectedCompany) e.target.style.borderColor = 'var(--glass-border, #e5e7eb)';
              }}
            />
            {selectedCompany && (
              <div style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                display: 'flex', alignItems: 'center', gap: 4
              }}>
                <Check size={16} style={{ color: '#10B981' }} />
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); setSelectedCompany(null); setCompanySearch(''); searchCompanies(''); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-tertiary, #9ca3af)', padding: 2
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Company list */}
          {!selectedCompany && (
            <div style={{
              border: '1px solid var(--glass-border, #e5e7eb)',
              borderRadius: 10, overflow: 'hidden',
              maxHeight: 240, overflowY: 'auto',
              background: 'var(--bg-primary, #fff)'
            }}>
              {searchLoading && (
                <div style={{
                  padding: '16px', textAlign: 'center',
                  fontSize: 13, color: 'var(--text-tertiary, #9ca3af)'
                }}>
                  Suche laeuft...
                </div>
              )}

              {!searchLoading && companyResults.length === 0 && (
                <div style={{
                  padding: '16px', textAlign: 'center',
                  fontSize: 13, color: 'var(--text-tertiary, #9ca3af)'
                }}>
                  {companySearch ? 'Keine Ergebnisse gefunden' : 'Suchbegriff eingeben...'}
                </div>
              )}

              {!searchLoading && companyResults.map((c, i) => (
                <div
                  key={c.id}
                  onClick={() => selectCompany(c)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    borderBottom: i < companyResults.length - 1 ? '1px solid var(--glass-border, #f3f4f6)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'background 0.1s'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent-primary-alpha, rgba(99,102,241,0.06))')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'rgba(99,102,241,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Building2 size={15} style={{ color: '#6366f1' }} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      fontWeight: 500, fontSize: 14,
                      color: 'var(--text-primary, #111)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {c.name}
                    </div>
                    <div style={{
                      fontSize: 12, color: 'var(--text-tertiary, #9ca3af)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {[c.industry, c.billing_city].filter(Boolean).join(' · ') || 'Keine Details'}
                    </div>
                  </div>
                </div>
              ))}

              {!searchLoading && companyResults.length >= 200 && (
                <div style={{
                  padding: '8px 14px', textAlign: 'center',
                  fontSize: 12, color: 'var(--text-tertiary, #9ca3af)',
                  background: 'var(--bg-secondary, #f9fafb)',
                  borderTop: '1px solid var(--glass-border, #e5e7eb)'
                }}>
                  Mehr als 200 Ergebnisse - bitte Suche verfeinern
                </div>
              )}
            </div>
          )}
        </div>

        {/* Contact selection */}
        {selectedCompany && (
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8,
              color: 'var(--text-primary, #111)'
            }}>
              Stellenauftrag (optional)
            </label>
            <select
              value={selectedJobOrderId}
              onChange={e => setSelectedJobOrderId(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10,
                border: '2px solid var(--glass-border, #e5e7eb)',
                background: 'var(--bg-secondary, #f9fafb)',
                color: 'var(--text-primary, #111)', fontSize: 14,
                boxSizing: 'border-box', outline: 'none'
              }}
            >
              <option value="">-- Ohne Stellenauftrag --</option>
              {jobOrders.map(a => (
                <option key={a.id} value={a.id}>
                  {a.title}
                  {a.location ? ` — ${a.location}` : ''}
                  {` (${Math.max((a.openings_count || 1) - (a.filled_count || 0), 0)} offen)`}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary, #9ca3af)', marginTop: 4 }}>
              {jobOrders.length === 0
                ? 'Für dieses Unternehmen ist kein offener Stellenauftrag hinterlegt.'
                : 'Nur mit Auftrag erscheint der Vorschlag in dessen Pipeline.'}
            </div>
          </div>
        )}

        {selectedCompany && (
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8,
              color: 'var(--text-primary, #111)'
            }}>
              Ansprechpartner (optional)
            </label>
            <select
              value={selectedContactId}
              onChange={e => setSelectedContactId(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10,
                border: '2px solid var(--glass-border, #e5e7eb)',
                background: 'var(--bg-secondary, #f9fafb)',
                color: 'var(--text-primary, #111)', fontSize: 14,
                boxSizing: 'border-box', outline: 'none'
              }}
            >
              <option value="">-- Keinen Ansprechpartner wählen --</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name} {c.title ? `(${c.title})` : ''} {c.email ? `- ${c.email}` : ''}
                </option>
              ))}
            </select>
            {contacts.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary, #9ca3af)', marginTop: 4 }}>
                Keine Ansprechpartner für dieses Unternehmen hinterlegt
              </div>
            )}
          </div>
        )}

        {/* Auto-send toggle */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
          padding: '12px 14px', borderRadius: 12,
          background: autoSend ? 'rgba(16, 185, 129, 0.06)' : 'var(--bg-secondary, #f9fafb)',
          border: `2px solid ${autoSend ? '#10B981' : 'var(--glass-border, #e5e7eb)'}`,
          transition: 'all 0.2s'
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: 6,
            border: `2px solid ${autoSend ? '#10B981' : '#d1d5db'}`,
            background: autoSend ? '#10B981' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.2s'
          }}>
            {autoSend && <Check size={14} style={{ color: '#fff' }} />}
          </div>
          <input
            type="checkbox"
            checked={autoSend}
            onChange={e => setAutoSend(e.target.checked)}
            style={{ display: 'none' }}
          />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #111)' }}>
              Direkt senden
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>
              Sofort an Kunden senden und Kandidat auf "Vorgestellt" setzen
            </div>
          </div>
        </label>
      </div>

      {/* Footer - wrapped in form for reliable submit */}
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[SubmissionModal] Form submitted!');
          handleSubmit();
        }}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 24px', borderTop: '1px solid var(--glass-border, #e5e7eb)',
          flexShrink: 0, margin: 0
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-tertiary, #9ca3af)' }}>
          {selectedCompany ? `An: ${selectedCompany.name}` : 'Kein Unternehmen ausgewählt'}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={() => handleClose()}
            style={{
              padding: '10px 20px', borderRadius: 10, fontSize: 14,
              border: '1px solid var(--glass-border, #e5e7eb)',
              background: 'transparent', cursor: 'pointer',
              color: 'var(--text-primary, #111)', fontWeight: 500
            }}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !selectedCompany}
            style={{
              padding: '10px 24px', borderRadius: 10, fontSize: 14,
              border: 'none',
              cursor: (loading || !selectedCompany) ? 'not-allowed' : 'pointer',
              background: autoSend
                ? 'linear-gradient(135deg, #10B981, #059669)'
                : 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#fff', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8,
              opacity: (loading || !selectedCompany) ? 0.5 : 1,
              boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
              transition: 'all 0.2s'
            }}
          >
            {autoSend ? <Send size={15} /> : <CheckCircle2 size={15} />}
            {loading ? 'Wird erstellt...' : autoSend ? 'Jetzt senden' : 'Als Entwurf speichern'}
          </button>
        </div>
      </form>
    </>
  );

  const renderSuccessStep = () => (
    <>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px', borderBottom: '1px solid var(--glass-border, #e5e7eb)',
        flexShrink: 0
      }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary, #111)', margin: 0 }}>
            {autoSend ? 'Vorstellung erfolgreich' : 'Entwurf gespeichert'}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', margin: '4px 0 0' }}>
            Möchtest du direkt ein Interview planen?
          </p>
        </div>
        <button
          type="button"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); handleClose(); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-tertiary, #9ca3af)', padding: 4
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Success Body */}
      <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
        {/* Success message */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '16px 18px', borderRadius: 12,
          background: 'rgba(16, 185, 129, 0.06)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          marginBottom: 24
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(16, 185, 129, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0
          }}>
            <PartyPopper size={20} style={{ color: '#10B981' }} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #111)' }}>
              {candidateName} wurde bei {selectedCompany?.name || createdSubmission?.client_company?.name} {autoSend ? 'vorgestellt' : 'als Entwurf angelegt'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>
              Status: {autoSend ? 'Gesendet' : 'Entwurf erstellt'}
            </div>
          </div>
        </div>

        {/* Interview scheduling */}
        <div style={{
          padding: '18px', borderRadius: 12,
          background: 'var(--bg-secondary, #f9fafb)',
          border: '1px solid var(--glass-border, #e5e7eb)'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16
          }}>
            <Calendar size={16} style={{ color: '#7C3AED' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #111)' }}>
              Interview planen
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4,
                color: 'var(--text-secondary, #6b7280)'
              }}>
                Datum
              </label>
              <input
                type="date"
                value={interviewDate}
                onChange={e => setInterviewDate(e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: '2px solid var(--glass-border, #e5e7eb)',
                  background: 'var(--bg-primary, #fff)',
                  color: 'var(--text-primary, #111)', fontSize: 13,
                  boxSizing: 'border-box', outline: 'none'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4,
                color: 'var(--text-secondary, #6b7280)'
              }}>
                Uhrzeit
              </label>
              <input
                type="time"
                value={interviewTime}
                onChange={e => setInterviewTime(e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: '2px solid var(--glass-border, #e5e7eb)',
                  background: 'var(--bg-primary, #fff)',
                  color: 'var(--text-primary, #111)', fontSize: 13,
                  boxSizing: 'border-box', outline: 'none'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{
              display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4,
              color: 'var(--text-secondary, #6b7280)'
            }}>
              Notizen (optional)
            </label>
            <textarea
              value={interviewNotes}
              onChange={e => setInterviewNotes(e.target.value)}
              placeholder="z.B. Ort, Teilnehmer, Vorbereitung..."
              rows={3}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8,
                border: '2px solid var(--glass-border, #e5e7eb)',
                background: 'var(--bg-primary, #fff)',
                color: 'var(--text-primary, #111)', fontSize: 13,
                boxSizing: 'border-box', outline: 'none',
                resize: 'vertical', fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer - wrapped in form for reliable submit */}
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          handleSaveInterview();
        }}
        style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          padding: '16px 24px', borderTop: '1px solid var(--glass-border, #e5e7eb)',
          flexShrink: 0, gap: 10, margin: 0
        }}
      >
        <button
          type="button"
          onClick={() => handleClose()}
          style={{
            padding: '10px 20px', borderRadius: 10, fontSize: 14,
            border: '1px solid var(--glass-border, #e5e7eb)',
            background: 'transparent', cursor: 'pointer',
            color: 'var(--text-primary, #111)', fontWeight: 500
          }}
        >
          Ohne Interview schließen
        </button>
        <button
          type="submit"
          disabled={savingInterview || !interviewDate}
          style={{
            padding: '10px 24px', borderRadius: 10, fontSize: 14,
            border: 'none',
            cursor: (savingInterview || !interviewDate) ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg, #7C3AED, #6D28D9)',
            color: '#fff', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: (savingInterview || !interviewDate) ? 0.5 : 1,
            boxShadow: '0 2px 8px rgba(124,58,237,0.3)',
            transition: 'all 0.2s'
          }}
        >
          <Clock size={15} />
          {savingInterview ? 'Wird gespeichert...' : 'Interview planen'}
        </button>
      </form>
    </>
  );

  const modalContent = (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        isolation: 'isolate'
      }}
      onMouseDown={e => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          handleClose();
        }
      }}
      onClick={e => e.stopPropagation()}
    >
      <div
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary, #fff)',
          borderRadius: 16, width: '100%', maxWidth: 580,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          border: '1px solid var(--glass-border, #e5e7eb)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          position: 'relative', zIndex: 1
        }}
      >
        {step === 'form' ? renderFormStep() : renderSuccessStep()}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SubmissionModal;
